import React from "react"
import { Modal, Form, Col, Button, Row, Table, Container, Image } from "react-bootstrap"
import { toast } from "react-toastify"
import ActionButton from "../../ActionButton"
import { APIError } from "../../../api/ObservableFromFetch"
import { NotAssignedPermissionsOfRoleGet, Permission, PermissionInRoleSave, PermissionsOfRoleGet, RoleCreate, RolesGet, UnassignedSubscriptionPermissionsGet } from "../../../api/Administration/Permissions"
import { forkJoin, Subscription } from "rxjs"
import { parseISO, format } from "date-fns"
import DatePicker from 'react-datepicker'
import Role from "../../../api/Roles/Model/Role"
import { EditPermission } from "./PermissionsSubscriptionModal"
import { BirthdayInput } from "../../FormattedInputs/BirthdayInput"
import NumericInput from 'react-numeric-input'
import { FaAngleDoubleDown, FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleDoubleUp, FaAngleDown, FaAngleUp, FaChevronLeft, FaChevronRight, FaPencilAlt } from "react-icons/fa"
import FormatDate from "../../../formatters/FormatDate"

interface PermissionsRoleModalProps {
  onCancel: () => void
  userName?: string
}

interface PermissionsRoleModalState {
  loading: boolean
  saving: boolean
  roles: Role[]
  selectedRole?: RoleData
  unassigned: Permission[]
  assigned: Permission[]
  notassigned: Permission[]
  editPermission?: EditPermission
  showAddNewModal: boolean
  showEditModal: boolean
  newRole?: RoleData
  titleDuplicated: boolean
  selectionLeft: number[]
  selectionRight: number[]
}

export interface RoleData {
  RoleID?: number
  Description?: string
  Level?: number
  Title?: string
  ActiveDate?: Date
  InActiveDate?: Date
  ModifiedBy?: string
}

export default class PermissionsRoleModalModal extends React.PureComponent<PermissionsRoleModalProps, PermissionsRoleModalState> {
  state: PermissionsRoleModalState = {
    loading: false,
    saving: false,
    roles: [],
    selectedRole: undefined,
    unassigned: [],
    assigned: [],
    notassigned: [],
    showAddNewModal: false,
    showEditModal: false,
    editPermission: undefined,
    newRole: undefined,
    titleDuplicated: false,
    selectionLeft: [],
    selectionRight: []
  }

  initialLoadSubscriptions?: Subscription
  loadSubscription?: Subscription
  updateSubscription?: Subscription
  activedate = React.createRef<DatePicker>()
  inactivedate = React.createRef<DatePicker>()
  roleActivedate = React.createRef<DatePicker>()
  roleInactivedate = React.createRef<DatePicker>()
  newRoleActivedate = React.createRef<DatePicker>()
  newRoleInactivedate = React.createRef<DatePicker>()

  componentDidMount() {
    this.loadInitialData()
  }

  componentWillUnmount() {
    this.initialLoadSubscriptions && this.initialLoadSubscriptions.unsubscribe()
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.updateSubscription && this.updateSubscription.unsubscribe()
  }

  loadInitialData() {
    this.initialLoadSubscriptions && this.initialLoadSubscriptions.unsubscribe()
    this.setState({ loading: true })
    this.initialLoadSubscriptions = forkJoin({
      roles: RolesGet(),
      unassigned: UnassignedSubscriptionPermissionsGet()
    })
      .subscribe(
        ({ roles, unassigned }) => {
          const roleID = this.state.selectedRole?.RoleID
          this.setState({ roles: [...roles], unassigned: [...unassigned], loading: false, selectedRole: undefined, selectionLeft: [], selectionRight: [] })
          if (roleID) this.selectRole(roleID)
        },
        (error) => {
          this.handleAPIError(error, { loading: false })
        }
      )
  }

  selectRole = (id: number) => {
    const role = this.state.roles.find(item => item.RoleID === id)
    if (role) {
      this.setState({ selectedRole: { RoleID: role?.RoleID, Title: role?.Title, Description: role?.Description, ActiveDate: parseISO(role?.ActiveDate || ""), InActiveDate: parseISO(role?.InActiveDate || ""), Level: role?.Level, ModifiedBy: this.props.userName || "" } })

      this.loadSubscription && this.loadSubscription.unsubscribe()
      this.loadSubscription = forkJoin({
        assigned: PermissionsOfRoleGet(role?.RoleID || 0, role?.Title || ""),
        notassigned: NotAssignedPermissionsOfRoleGet(role?.RoleID || 0, role?.Title || "")
      })
        .subscribe(
          ({ assigned, notassigned }) => this.setState({ assigned: [...assigned], notassigned: [...notassigned], loading: false }),
          (error) => {
            this.handleAPIError(error, { loading: false })
          }
        )
    }
    else {this.setState({ selectedRole: undefined})}
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  handleSubmit = () => {
    this.updateSubscription && this.updateSubscription.unsubscribe()
    this.setState({ saving: true })
    const { selectedRole, assigned } = this.state

    this.updateSubscription = PermissionInRoleSave(selectedRole, assigned).subscribe(
      data => {
        this.setState({ saving: false })
        toast.success(data)
        this.loadInitialData()
      },
      error => this.handleAPIError(error, { saving: false })
    )
   }

  validSubmit = () => {
    const { selectedRole } = this.state
    return !!selectedRole && !!selectedRole.Title && !!selectedRole.Description && !!selectedRole.ActiveDate && !!selectedRole.InActiveDate && !!selectedRole.Level
  }

  handleEdit = (permission: Permission) => this.setState({ showEditModal: true, editPermission: { ID: permission.PermissionID, Title: permission.Title, ActiveDate: parseISO(permission.ActiveDate || ""), InactiveDate: parseISO(permission.InActiveDate || "") } })
  handleEditCancel = () => { this.setState({ showEditModal: false, editPermission: undefined }) }
  handleEditSubmit = () => {
    const { editPermission, assigned } = this.state
    const relatedPermission = assigned.find(item => item.PermissionID === editPermission?.ID)

    if (relatedPermission) {
      relatedPermission.ActiveDate = format(editPermission?.ActiveDate || 0, "yyyy-MM-dd'T'HH:mm:ss")
      relatedPermission.InActiveDate = format(editPermission?.InactiveDate || 0, "yyyy-MM-dd'T'HH:mm:ss")
    }

    this.setState({ showEditModal: false, editPermission: undefined })
  }

  editValid = () => {
    const { editPermission } = this.state
    return !!editPermission && !!editPermission.ActiveDate && !!editPermission.InactiveDate
   }

  handleAddNew = () => { this.setState({ showAddNewModal: true }) }
  handleAddNewCancel = () => { this.setState({ showAddNewModal: false, newRole: undefined }) }
  handleAddNewSubmit = () => {
    this.updateSubscription && this.updateSubscription.unsubscribe()
    this.setState({ saving: true })
    const { newRole } = this.state
    const data = {
      Description: newRole?.Description,
      Level: newRole?.Level,
      Title: newRole?.Title,
      ActiveDate: newRole?.ActiveDate,
      InActiveDate: newRole?.InActiveDate,
      ModifiedBy: this.props.userName
    }

    this.updateSubscription = RoleCreate(data).subscribe(
      data => {
        this.setState({ saving: false, showAddNewModal: false, loading: true })
        toast.success(data)
        RolesGet().subscribe(
          data => this.setState({ roles: data, loading: false, selectedRole: undefined }),
          error => this.handleAPIError(error, { loading: false })
        )
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  addNewValid = () => {
    const { newRole, titleDuplicated } = this.state
    return !!newRole && !!newRole.Title && !!newRole.Description && !!newRole.ActiveDate && !!newRole.InActiveDate && !!newRole.Level && !titleDuplicated
  }

   handleNewTitleChange = (title: string) => {
    this.setState({ newRole: {...this.state.newRole, Title: title}, titleDuplicated: this.validateTitle(title)})
   }

   validateTitle = (title: string) => { return this.state.roles.some(role => role.Title === title) }

  handleRoleActiveDateChange = (date: Date) => {
    const { selectedRole } = this.state
    if (selectedRole) {
      const inactiveDate = selectedRole?.InActiveDate
      const dateiInPast: boolean = inactiveDate instanceof Date && inactiveDate < new Date
      const allowChange = inactiveDate instanceof Date ? date < inactiveDate || dateiInPast : true
      this.setState({ selectedRole: {...selectedRole, ActiveDate: (allowChange ? date : undefined), InActiveDate: (dateiInPast ? undefined : inactiveDate)}})
    }
  }

  handleRoleInActiveDateChange = (date: Date) => {
    const { selectedRole } = this.state
    if (selectedRole) {
      const activeDate = selectedRole?.ActiveDate
      const allowChange = activeDate instanceof Date ?  date > activeDate : true
      this.setState({ selectedRole: {...selectedRole, InActiveDate: (allowChange ? date : undefined)}})
    }
  }

  handleActiveDateChange = (date: Date) => {
    const inactiveDate = this.state.editPermission?.InactiveDate
    const dateiInPast: boolean = inactiveDate instanceof Date && inactiveDate < new Date
    const allowChange = inactiveDate instanceof Date ? date < inactiveDate || dateiInPast : true
    this.setState({ editPermission: { ...this.state.editPermission, ActiveDate: allowChange ? date : undefined, InactiveDate: dateiInPast ? undefined : inactiveDate } })
  }

  handleInActiveDateChange = (date: Date) => {
    const activeDate = this.state.editPermission?.ActiveDate
    const allowChange = activeDate instanceof Date ? date > activeDate : true
    this.setState({ editPermission: { ...this.state.editPermission, InactiveDate: allowChange ? date : undefined } })
  }

  move(dir: "ltr" | "rtl", all?: boolean) {
    if (dir === "ltr") {
      const ids = !!all ? this.state.notassigned.map(item => item.PermissionID) : this.state.selectionLeft
      this.setState({
        selectionLeft: [],
        notassigned: this.state.notassigned.filter(item => !ids.includes(item.PermissionID)),
        assigned: [...this.state.assigned, ...this.state.notassigned.filter(item => ids.includes(item.PermissionID))]
      })
    } else {
      const ids = !!all ? this.state.assigned.map(item => item.PermissionID) : this.state.selectionRight
      this.setState({
        selectionRight: [],
        notassigned: [...this.state.notassigned, ...this.state.assigned.filter(item => ids.includes(item.PermissionID))],
        assigned: this.state.assigned.filter(item => !ids.includes(item.PermissionID))
      })
    }
  }

  handleRightItemClick = (id: number) => {
    if (this.state.selectionRight.includes(id)) {
      this.setState({ selectionRight: this.state.selectionRight.filter(item => item !== id) })
    } else {
      this.setState({ selectionRight: [...this.state.selectionRight, id] })
    }
  }

  render() {
    const { loading, saving, selectedRole, roles, showAddNewModal, showEditModal, editPermission, newRole, titleDuplicated, selectionRight, selectionLeft } = this.state
    const left = !selectedRole ? this.state.unassigned : this.state.notassigned
    const right = !selectedRole ? [] : this.state.assigned
    const disabled = !selectedRole || loading

    return (
      <>
        <Modal.Dialog size="xl">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Role/Permissions</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Row className="form-group">
              <Form.Label column sm={2} htmlFor="role">Role</Form.Label>
              <Col sm={3}>
                <Form.Control id="role" as="select" value={selectedRole?.RoleID?.toString()} onChange={(e) => this.selectRole(Number(e.target.value))}>
                  <option value="">Select Role</option>
                  {roles && roles.map((role: Role) => <option value={role.RoleID} key={role.RoleID}>{role.Title}</option>)}
                </Form.Control>
              </Col>
              <Col sm={1}></Col>
              <Form.Label column sm={2} htmlFor="roleActivedate">Active Date</Form.Label>
              <Col sm={3}>
                <DatePicker className="form-control"
                    ref={this.roleActivedate}
                    required={!!selectedRole}
                    showMonthDropdown
                    showYearDropdown
                    disabled={!selectedRole}
                    minDate={new Date()}
                    dropdownMode="select"
                    popperPlacement="right"
                    selected={selectedRole ? selectedRole?.ActiveDate : undefined}
                    enableTabLoop={false}
                    onKeyDown={(e) => { if (this.roleActivedate && this.roleActivedate.current && e.key === 'Tab') { this.roleActivedate.current.setOpen(false) } }}
                    onChange={date => this.handleRoleActiveDateChange(date as Date)}
                    customInput={<BirthdayInput />} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column sm={2} htmlFor="roleLevel">Level</Form.Label>
              <Col sm={3}>
                <Form.Control as={NumericInput} id="roleLevel" min={0} value={selectedRole?.Level} disabled={!selectedRole} isInvalid={selectedRole && selectedRole.Level === undefined} strict onChange={(value: any) => this.setState({ selectedRole: {...selectedRole, Level: value as number}})} />
              </Col>
              <Col sm={1}></Col>
              <Form.Label column sm={2} htmlFor="roleInActivedate">Inactive Date</Form.Label>
              <Col sm={3}>
                <DatePicker className="form-control"
                    ref={this.roleInactivedate}
                    required={!!selectedRole}
                    showMonthDropdown
                    showYearDropdown
                    disabled={!selectedRole}
                    minDate={selectedRole?.ActiveDate instanceof Date ? selectedRole?.ActiveDate : new Date()}
                    dropdownMode="select"
                    popperPlacement="right"
                    selected={selectedRole ? selectedRole?.InActiveDate : undefined}
                    enableTabLoop={false}
                    onKeyDown={(e) => { if (this.roleInactivedate && this.roleInactivedate.current && e.key === 'Tab') { this.roleInactivedate.current.setOpen(false) } }}
                    onChange={date => this.handleRoleInActiveDateChange(date as Date)}
                    customInput={<BirthdayInput />} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column sm={2} htmlFor="roleDesc">Description</Form.Label>
              <Col sm={9}>
                <Form.Control as="textarea"
                  rows={4}
                  isInvalid={selectedRole && selectedRole.Description === ""}
                  disabled={!selectedRole}
                  value={selectedRole?.Description || ""}
                  onChange={(e) => this.setState({ selectedRole: {...selectedRole, Description: e.target.value}})} />
              </Col>
            </Form.Row>
            <hr />
            <Form className="px-4" hidden={loading}>
              <Row className="d-none d-lg-flex">
                <Col lg={4}>Permissions Not Assigned</Col>
                <Col lg={1} className="px-0"></Col>
                <Col lg={7} className="pl-lg-0">Permissions Assigned</Col>
              </Row>
              <Row>
                <Col className="d-lg-none">Permissions Not Assigned</Col>
                <Col lg={4} className="pr-lg-0">
                  <Form.Control as="select" multiple className="grey-selection" style={{ height: "288px", boxShadow: "none" }}
                    disabled={false}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { this.setState({ selectionLeft: Array.from(e.target.selectedOptions, option => Number(option.value)) }) }}>
                    {left.map(item => <option key={item.PermissionID} value={item.PermissionID}>{item.Title}</option>)}
                  </Form.Control>
                </Col>
                <Col lg={1} className="px-0">
                  <Container fluid>
                    <Row>
                      <Col className="px-1 text-center">
                        <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!left.length || disabled} onClick={() => this.move("ltr", true)}>
                          <FaAngleDoubleRight className="d-none d-lg-inline" />
                          <FaAngleDoubleDown className="d-lg-none" />
                        </Button>
                      </Col>
                      <Col className="px-1 text-center">
                        <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!selectionLeft.length || disabled} onClick={() => this.move("ltr")}>
                          <FaChevronRight className="d-none d-lg-inline" />
                          <FaAngleDown className="d-lg-none" />
                        </Button>
                      </Col>
                      <Col className="px-1 text-center">
                        <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!selectionRight.length || disabled} onClick={() => this.move("rtl")}>
                          <FaChevronLeft className="d-none d-lg-inline" />
                          <FaAngleUp className="d-lg-none" />
                        </Button>
                      </Col>
                      <Col className="px-1 text-center">
                        <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!right.length || disabled} onClick={() => this.move("rtl", true)}>
                          <FaAngleDoubleLeft className="d-none d-lg-inline" />
                          <FaAngleDoubleUp className="d-lg-none" />
                        </Button>
                      </Col>
                    </Row>
                  </Container>
                </Col>
                <Col className="d-lg-none">Permissions Assigned</Col>
                <Col lg={7} className="pl-lg-0" style={{ height: "288px", overflowY: "auto" }}>
                  <Table className="table-sm dark" borderless responsive>
                    <thead>
                      <tr>
                        <th>Permission</th>
                        <th>Status</th>
                        <th>Active</th>
                        <th>Inactive</th>
                        <th>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {right.map(item => <tr key={item.PermissionID} className={selectionRight.includes(item.PermissionID) ? "selected" : ""} onClick={() => this.handleRightItemClick(item.PermissionID)}>
                        <td>{item.Title}</td>
                        <td>{item.RecordStatusType}</td>
                        <td>{FormatDate(item.ActiveDate)}</td>
                        <td>{FormatDate(item.InActiveDate)}</td>
                        <td>
                          <Button variant="icon-link" size="sm" className="btn-icon-primary" onClick={() => { this.handleEdit(item) }}>
                            <FaPencilAlt />
                          </Button>
                        </td>
                      </tr>)}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <ActionButton variant="primary" onClick={this.handleAddNew} disabled={!!this.state.selectedRole} >Add New</ActionButton>
            <Button variant="secondary" className="mr-2" onClick={() => this.props.onCancel()}>Cancel</Button>
            <ActionButton variant="primary" onClick={this.handleSubmit} disabled={loading || saving || !this.validSubmit()} >Submit</ActionButton>
          </Modal.Footer>
        </Modal.Dialog>

        <Modal show={showAddNewModal} onHide={() => this.handleAddNewCancel()} size="lg" backdrop="static" dialogClassName="dark">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Add New Role</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Row className="form-group required">
              <Form.Label column sm={2} htmlFor="newName">Role</Form.Label>
              <Col sm={3}>
                <Form.Control id="newName" type="text" value={newRole?.Title} isInvalid={(newRole && !newRole.Title) || titleDuplicated} onChange={(e) => this.handleNewTitleChange(e.target.value)} />
                {titleDuplicated && <Form.Control.Feedback type="invalid" className="d-block">The Role Already Exists. Please reenter or modify and press the next button."</Form.Control.Feedback>}
              </Col>
              <Col sm={1}></Col>
              <Form.Label column sm={2} htmlFor="newRoleActivedate">Active Date</Form.Label>
              <Col sm={3}>
                <DatePicker className="form-control"
                    ref={this.newRoleActivedate}
                    required={!!newRole}
                    showMonthDropdown
                    showYearDropdown
                    minDate={new Date()}
                    maxDate={newRole?.InActiveDate}
                    dropdownMode="select"
                    popperPlacement="right"
                    selected={newRole ? newRole?.ActiveDate : undefined}
                    enableTabLoop={false}
                    onKeyDown={(e) => { if (this.newRoleActivedate && this.newRoleActivedate.current && e.key === 'Tab') { this.newRoleActivedate.current.setOpen(false) } }}
                    onChange={date => this.setState({ newRole: {...newRole, ActiveDate: date as Date}})}
                    customInput={<BirthdayInput />} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column sm={2} htmlFor="newLevel">Level</Form.Label>
              <Col sm={3}>
                <Form.Control as={NumericInput} id="newLevel" min={0} value={newRole?.Level} isInvalid={newRole && newRole.Level === undefined} required={!!newRole} strict onChange={(value: any) => this.setState({ newRole: {...newRole, Level: value as number}})} />
              </Col>
              <Col sm={1}></Col>
              <Form.Label column sm={2} htmlFor="newRoleInActivedate">Inactive Date</Form.Label>
              <Col sm={3}>
                <DatePicker className="form-control"
                    ref={this.newRoleInactivedate}
                    required={!!newRole}
                    showMonthDropdown
                    showYearDropdown
                    minDate={newRole?.ActiveDate}
                    dropdownMode="select"
                    popperPlacement="right"
                    selected={newRole ? newRole?.InActiveDate : undefined}
                    enableTabLoop={false}
                    onKeyDown={(e) => { if (this.newRoleInactivedate && this.newRoleInactivedate.current && e.key === 'Tab') { this.newRoleInactivedate.current.setOpen(false) } }}
                    onChange={date => this.setState({ newRole: {...newRole, InActiveDate: date as Date}})}
                    customInput={<BirthdayInput />} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column sm={2} htmlFor="newDescription">Description</Form.Label>
                <Col sm={9}>
                  <Form.Control as="textarea"
                    rows={4}
                    isInvalid={newRole && !newRole.Description}
                    value={newRole?.Description}
                    onChange={(e) => this.setState({ newRole: {...newRole, Description: e.target.value} })} />
                </Col>
            </Form.Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" className="mr-2" onClick={() => this.handleAddNewCancel()}>Cancel</Button>
            <ActionButton variant="primary" onClick={() => this.handleAddNewSubmit()} disabled={!this.addNewValid()} spin={saving} disableOnSpin>Next</ActionButton>
          </Modal.Footer>
        </Modal>

        {!!editPermission && <Modal show={showEditModal} onHide={() => this.handleEditCancel()} size="lg" backdrop="static" dialogClassName="dark">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Edit Permission</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Row className="form-group required">
              <Form.Label column lg={2} htmlFor="editSubs" className="required">Role</Form.Label>
              <Col lg={4}>
                <Form.Control id="editSubs" type="text" defaultValue={selectedRole?.Title} disabled={true} />
              </Col>
              <Col lg={1}></Col>
              <Form.Label column lg={2} htmlFor="editActive">Active Date</Form.Label>
              <Col lg={3}>
                <DatePicker className="form-control"
                  ref={this.activedate}
                  required={true}
                  showMonthDropdown
                  showYearDropdown
                  minDate={new Date()}
                  dropdownMode="select"
                  popperPlacement="right"
                  selected={editPermission?.ActiveDate}
                  enableTabLoop={false}
                  onKeyDown={(e) => { if (this.activedate && this.activedate.current && e.key === 'Tab') { this.activedate.current.setOpen(false) } }}
                  onChange={date => this.handleActiveDateChange(date as Date)}
                  customInput={<BirthdayInput />} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column lg={2} htmlFor="editPerm" className="required">Permission</Form.Label>
              <Col lg={4}>
                <Form.Control id="editPerm" type="text" defaultValue={editPermission?.Title} disabled={true} />
              </Col>
              <Col lg={1}></Col>
              <Form.Label column lg={2} htmlFor="editInactive">Inactive Date</Form.Label>
              <Col lg={3}>
                <DatePicker className="form-control"
                  ref={this.inactivedate}
                  required={true}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  popperPlacement="right"
                  minDate={editPermission?.ActiveDate instanceof Date ? editPermission?.ActiveDate : new Date()}
                  selected={editPermission?.InactiveDate}
                  enableTabLoop={false}
                  onKeyDown={(e) => { if (this.inactivedate && this.inactivedate.current && e.key === 'Tab') { this.inactivedate.current.setOpen(false) } }}
                  onChange={date => this.handleInActiveDateChange(date as Date)}
                  customInput={<BirthdayInput />} />
              </Col>
            </Form.Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" className="mr-2" onClick={() => this.handleEditCancel()}>Cancel</Button>
            <ActionButton variant="primary" onClick={() => this.handleEditSubmit()} disabled={!this.editValid()} spin={saving} disableOnSpin>Finish</ActionButton>
          </Modal.Footer>
        </Modal>}
      </>
    )
  }
}
